import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  api,
  clearAuthTokens,
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
} from '../../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [showNewUserWelcome, setShowNewUserWelcome] = useState(false);
  const [followRefreshPrompt, setFollowRefreshPrompt] = useState(null);
  const [loading, setLoading] = useState(Boolean(getAuthToken() || getRefreshToken()));
  const [authPrompt, setAuthPrompt] = useState(null);
  const [loginSuccessAt, setLoginSuccessAt] = useState(0);

  useEffect(() => {
    let mounted = true;

    async function loadUser() {
      if (!getAuthToken()) {
        if (!getRefreshToken()) {
          setLoading(false);
          return;
        }

        try {
          const refreshed = await api.refreshAuth();
          if (mounted) {
            setUser(refreshed.user || null);
            setFollowRefreshPrompt(refreshed.follow_refresh_hint?.stale ? refreshed.follow_refresh_hint : null);
          }
          return;
        } catch {
          clearAuthTokens();
          if (mounted) setUser(null);
          return;
        } finally {
          if (mounted) setLoading(false);
        }
      }

      try {
        const res = await api.getMe();
        if (mounted) setUser(res.user || null);
      } catch {
        clearAuthTokens();
        if (mounted) setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadUser();

    return () => {
      mounted = false;
    };
  }, []);

  const completeLogin = useCallback(async (token, nextUser, refreshToken, options = {}) => {
    setAuthToken(token);
    setRefreshToken(refreshToken);
    setUser(nextUser);
    setShowNewUserWelcome(Boolean(options.isNewUser));
    setFollowRefreshPrompt(options.followRefreshHint?.stale ? options.followRefreshHint : null);
    setLoginSuccessAt(Date.now());
    const afterLogin = authPrompt?.afterLogin;
    setAuthPrompt(null);
    if (typeof afterLogin === 'function') {
      window.setTimeout(afterLogin, 0);
    }
  }, [authPrompt]);

  const requestLogin = useCallback((options = {}) => {
    if (user) {
      if (typeof options.afterLogin === 'function') options.afterLogin();
      return;
    }
    setAuthPrompt({
      title: options.title || '',
      copy: options.copy || '',
      afterLogin: options.afterLogin,
    });
  }, [user]);

  const closeLoginPrompt = useCallback(() => {
    setAuthPrompt(null);
  }, []);

  function dismissNewUserWelcome() {
    setShowNewUserWelcome(false);
  }

  function dismissFollowRefreshPrompt() {
    setFollowRefreshPrompt(null);
  }

  async function logout() {
    try {
      await api.logout();
    } catch {
      // Local logout still wins even if the network is unavailable.
    }
    clearAuthTokens();
    setUser(null);
    setFollowRefreshPrompt(null);
  }

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      showNewUserWelcome,
      followRefreshPrompt,
      completeLogin,
      requestLogin,
      closeLoginPrompt,
      authPrompt,
      loginSuccessAt,
      dismissNewUserWelcome,
      dismissFollowRefreshPrompt,
      logout,
    }),
    [authPrompt, closeLoginPrompt, completeLogin, followRefreshPrompt, loading, loginSuccessAt, requestLogin, showNewUserWelcome, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth() {
  return useContext(AuthContext);
}
